export type BillingCycle = 'monthly' | 'yearly';

export type SubscriptionStatus =
  | 'CREATED'
  | 'AUTHENTICATED'
  | 'ACTIVE'
  | 'PENDING'
  | 'HALTED'
  | 'PAUSED'
  | 'CANCELLED'
  | 'EXPIRED';

export type PaymentStatus = 'CREATED' | 'AUTHORIZED' | 'CAPTURED' | 'FAILED' | 'REFUNDED' | 'PENDING';

export type PaymentMethod = 'card' | 'upi' | 'emandate' | 'netbanking' | 'wallet' | 'unknown' | 'free';

export type DiscountType = 'PERCENT' | 'FLAT';

export type BillingErrorCode =
  | 'CARD_DECLINED'
  | 'PAYMENT_TIMEOUT'
  | 'DUPLICATE_SUBSCRIPTION'
  | 'PLAN_CHANGE_ALREADY_SCHEDULED'
  | 'PLAN_CHANGE_NOT_ALLOWED'
  | 'NETWORK_ERROR'
  | 'UPI_PENDING'
  | 'PLAN_UNAVAILABLE'
  | 'COUPON_INVALID'
  | 'UNKNOWN';

export interface BillingApiErrorShape {
  code: BillingErrorCode;
  message: string;
  eventId?: string;
}

export class BillingApiError extends Error implements BillingApiErrorShape {
  code: BillingErrorCode;
  eventId?: string;

  constructor(payload: BillingApiErrorShape) {
    super(payload.message);
    this.name = 'BillingApiError';
    this.code = payload.code;
    this.eventId = payload.eventId;
  }
}

export type Plan = {
  id: string;
  name: string;
  displayName: string;
  description: string;
  priceMonthly: number; // INR
  priceYearly: number; // INR
  features: string[];
  isActive: boolean;
  sortOrder: number;
};

export type Subscription = {
  id: string;
  userId: string;
  planId: string;
  planName: string;
  billingCycle: BillingCycle;
  status: SubscriptionStatus;
  paymentMethod: PaymentMethod;
  currentPeriodStart?: string; // ISO
  currentPeriodEnd?: string; // ISO
  gracePeriodEnd?: string; // ISO
  cancelAtPeriodEnd?: boolean;
  cancelledAt?: string; // ISO

  hasScheduledChanges?: boolean;
  pendingPlanId?: string;
  pendingBillingCycle?: BillingCycle;
  pendingChangeType?: 'UPGRADE' | 'DOWNGRADE' | 'CANCELLATION' | 'REACTIVATION';
  pendingChangeMethod?: string;
  pendingEffectiveAt?: string; // ISO
};

export type CreateSubscriptionInput = {
  planId: string;
  billingCycle: BillingCycle;
  couponCode?: string;
};

export type CreateSubscriptionResult = {
  subscriptionId: string;
  requiresCheckout: boolean;
  checkoutKeyId?: string;
  razorpaySubscriptionId?: string;
  checkoutMessage?: string;
};

export type ChangePlanInput = {
  newPlanId: string;
  billingCycle: BillingCycle;
};

export type ChangePlanResult = {
  requiresCheckout: boolean;
  checkoutKeyId?: string;
  razorpaySubscriptionId?: string;
  effectiveAt?: string; // ISO
  checkoutMessage?: string;
};

export type RetryCheckoutResult = {
  requiresCheckout: boolean;
  checkoutKeyId?: string;
  razorpaySubscriptionId?: string;
  checkoutMessage?: string;
};

// Zopeo billing currently uses subscription checkout.
// razorpay_order_id remains typed for Razorpay compatibility, but the backend
// rejects order verification until an owned local order/payment mapping exists.
export type VerifyPaymentInput = {
  razorpay_payment_id?: string;
  razorpay_order_id?: string;
  razorpay_subscription_id?: string;
  razorpay_signature?: string;
};

export type VerifyPaymentResult = {
  verified: boolean;
  eventId: string;
};

export type ProrationPreview = {
  currency: string; // INR
  chargeToday: number; // INR
  creditAmount: number; // INR
  nextChargeAmount: number; // INR
  nextBillingDate: string; // ISO date
  daysRemaining: number | null;
  currentPlanLabel: string;
  newPlanLabel: string;
  notes: string[];
};

export type CouponValidationState = 'idle' | 'validating' | 'valid' | 'invalid' | 'applied';

export type Coupon = {
  code: string;
  discountType: DiscountType;
  discountValue: number;
  description?: string;
};

export type CouponValidationResult =
  | { ok: true; coupon: Coupon; discountedAmountMonthly?: number; discountedAmountYearly?: number }
  | { ok: false; code: BillingErrorCode; message: string; eventId?: string };

export type PaymentHistoryItem = {
  id: string;
  createdAt: string; // ISO
  description: string;
  amount: number; // INR
  currency: string;
  status: PaymentStatus;
  method: PaymentMethod;
  // Razorpay payment ids must never be persisted to local/session storage.
  // This field is intended for backend-provided history only.
  razorpayPaymentId?: string;
  // Internal-only reference safe to persist in mocks.
  internalRef?: string;
  invoiceId?: string;
  invoicePdfUrl?: string | null;
  refundedAmount?: number;
};

export type InvoiceItem = {
  id: string;
  createdAt: string; // ISO
  invoiceNumber: string;
  amount: number;
  currency: string;
  status: 'DRAFT' | 'PAID' | 'EXPIRED';
  // Backend will return a short-lived presigned S3 URL (15 min expiry).
  // Call GET /api/payments/invoices/:id/download to get a fresh URL on demand
  // rather than storing the URL — presigned URLs expire.
  pdfUrl?: string | null;
};

// Track C (C1): current-period per-booking usage — GET /api/payments/usage/me.
// Amounts are in rupees (numbers). planLabel is the tier snapshot, null if nothing accrued yet.
export type UsageSummary = {
  billingPeriod: string;       // "yyyy-MM"
  confirmedBookings: number;   // served bookings metered this period (billable + free)
  billableBookings: number;    // bookings actually accruing the fee (non-shadow)
  accruedAmount: number;       // rupees accrued so far this period
  currency: string;            // ISO currency, e.g. "INR"
  planLabel: string | null;    // Free / Pro / Premium
  nextInvoiceEstimate: number; // what the next monthly invoice would total if billed now
};
